import { ITask } from 'Core/TaskAdapter'
import {
    AttributeDefinitionType,
    TaskAttribute,
    ContainerType,
    TaskConfigType,
    TaskMode,
    JSONAnnotationDataType,
} from 'Types/Tasks.types'
import { createReactElement, UpdatableReactElement } from 'shared/utils/react.utils'
import { JSONAnnotationTaskUi } from 'Tasks/JSONAnnotation/UI/JSONAnnotationTaskUI'
import { Studio } from 'Core/Studio'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { forEach } from 'lodash'
import { JSONAnnotationContent } from './UI/JSONAnnotationContent'
import { JSONAnnotationContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/JSONAnnotationContainerStrategy'
import { AttributeGroupEvent } from 'Core/AttributeCore/AttributeGroup/BaseAttributeGroup'
import { JSONPath } from 'jsonpath-plus'
import { jsonPathToDotNotation } from './utils'

export class JSONAnnotationTask
    extends CommonTask<ContainerType.JSONAnnotation, JSONAnnotationDataType>
    implements ITask<TaskConfigType<ContainerType.JSONAnnotation, JSONAnnotationDataType>>
{
    protected jsonAnnotationUI?: UpdatableReactElement
    private showFillMandatoryFieldsError: boolean = false

    constructor(protected studio: Studio) {
        const containerStrategy = new JSONAnnotationContainerStrategy()
        super(studio, containerStrategy)
        containerStrategy.setContext({ task: this })
    }

    public initialize(
        config: TaskConfigType<ContainerType.JSONAnnotation, JSONAnnotationDataType>
    ) {
        this.jsonAnnotationUI = createReactElement(JSONAnnotationContent, {
            getShowFillMandatoryFieldsError: this.getShowFillMandatoryFieldsError,
            setShowFillMandatoryFieldsError: this.setShowFillMandatoryFieldsError,
            isReadOnly: () => config.mode !== TaskMode.Triager && this.isReadOnly,
            mode: config.mode,
            content:
                config.assignment.task.input_container.data.data.json_annotation?.content.value ??
                {},
        })

        this.studio.workbench.enqueueUiUpdate(workbenchUi => {
            this.ui = createReactElement(JSONAnnotationTaskUi, {
                jsonAnnotationUI: this.jsonAnnotationUI!,
            })

            this.addCommonWorkbenchComponents(workbenchUi, config)

            workbenchUi.setShouldCloseOppositeSidebar(true)

            // add json path attributes
            this.addJSONPathAttributes()

            // add rate/review if attributes are present
            if (this.hasEditableAttributes(config)) {
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    groupKey: 'quality-evaluation',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: { useInCollection: true },
                })
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.EXTRA_INFO,
                    groupKey: 'extra-info',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.EXTRA_INFO
                    ),
                    config: { useInCollection: true },
                })
                workbenchUi.rightSidebar.addDivider()
            }
        })
    }

    private addJSONPathAttributes() {
        const config = this.initialConfig as TaskConfigType<
            ContainerType.JSONAnnotation,
            JSONAnnotationDataType
        >
        const pathLevelAttributes = config.assignment.task.attributes.filter(
            attribute => attribute.type === AttributeDefinitionType.JSON_PATH
        )
        const jsonContent =
            config.assignment.task.input_container.data.data.json_annotation?.content.value

        if (!jsonContent) {
            throw new Error('JSON content is not found')
        }

        const linkedPathToPossiblePaths: Record<string, string[]> = {}
        const possiblePathToTransformedPath: Record<string, string> = {}
        const pathToAttributes: Record<string, TaskAttribute[]> = {}

        forEach(pathLevelAttributes, attribute => {
            const jsonPath = attribute.linked_to_path
            if (!jsonPath) throw new Error('jsonPath not found for path level attribute')
            let possiblePaths: string[]
            if (linkedPathToPossiblePaths[jsonPath]) {
                possiblePaths = linkedPathToPossiblePaths[jsonPath]
            } else {
                possiblePaths = JSONPath({
                    path: jsonPath,
                    resultType: 'path',
                    json: jsonContent,
                })
                linkedPathToPossiblePaths[jsonPath] = possiblePaths
            }
            forEach(possiblePaths, path => {
                let transformedPath: string
                if (possiblePathToTransformedPath[path]) {
                    transformedPath = possiblePathToTransformedPath[path]
                } else {
                    transformedPath = jsonPathToDotNotation(path, jsonContent)
                    possiblePathToTransformedPath[path] = transformedPath
                }
                if (pathToAttributes[transformedPath]) {
                    pathToAttributes[transformedPath].push(attribute)
                } else {
                    pathToAttributes[transformedPath] = [attribute]
                }
            })
        })

        forEach(pathToAttributes, (attributes, path) => {
            const attributeConfig = {
                type: AttributeDefinitionType.TARGETED_ATTRIBUTES,
                groupKey: path,
                attributes,
                config: { useInCollection: false, props: { header: '' } },
            }
            this.attributeManager?.useAttributeGroup(attributeConfig)
        })

        const attributeGroups = this.attributeManager?.getAttributeGroupsByDefinitionType(
            AttributeDefinitionType.TARGETED_ATTRIBUTES
        )
        attributeGroups?.forEach(group => {
            group.on(AttributeGroupEvent.Change, () => {
                this.jsonAnnotationUI?.updater.forceUpdate()
            })
        })
    }

    protected onEnableEditResponse() {
        super.onEnableEditResponse()
        this.jsonAnnotationUI?.updater.forceUpdate()
    }

    getShowFillMandatoryFieldsError = () => {
        return this.showFillMandatoryFieldsError
    }

    setShowFillMandatoryFieldsError = (show: boolean) => {
        this.showFillMandatoryFieldsError = show
        this.jsonAnnotationUI?.updater.forceUpdate()
    }

    validateData = async () => {
        const isJSONPathAttributesValid =
            await this.attributeManager?.validateGroupsByDefinitionType(
                AttributeDefinitionType.TARGETED_ATTRIBUTES
            )
        if (isJSONPathAttributesValid) {
            this.showFillMandatoryFieldsError && this.setShowFillMandatoryFieldsError(false)
        } else {
            this.setShowFillMandatoryFieldsError(true)
        }
        return this.attributeManager?.validateGroups()
    }
}
